import React, { useEffect, useContext, useState, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { OrganizationContext } from "../contexts/OrganizationContext";
import { ProfileContext } from "../contexts/ProfileContext";
import { CaretLeft, CaretRight, Gear, X } from "@phosphor-icons/react";
import { OrganizationDetailsModal } from "./organization-details-modal";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import { useReactToPrint } from "react-to-print";
import {
  doc,
  updateDoc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../js/firebase";
import { disableOrganization } from "../services/organization-service";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../helpers/useToast";

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

//the component that displays the different organizations and thier info

export const ViewOrganizations = () => {
  const {
    organizations,
    organizationNumbers,
    filterByArray,
    filterValueArray,
    filterOrganizations,
    setFilterByArray,
    setFilterValueArray,
    setShowAllOrganizations,
    nameClicked,
    setNameClicked,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    callMetricsData,
    fetchCallMetrics,
  } = useContext(OrganizationContext);
  const {
    setOrganizationNumber,
    setOrganization,
    setShowReports,
    worker,
    setViewingAsOrganization,
    organizationFirebaseId,
  } = useContext(ProfileContext);
  const navigate = useNavigate();

  // State for the selected organization to view
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [holdMusic, setHoldMusic] = useState("");
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const componentToPrint = useRef();
  const selectRef = useRef(null);

  // Settings modal states
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedOrgForSettings, setSelectedOrgForSettings] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [zohoAccountId, setZohoAccountId] = useState("");
  const [welcomeAudio, setWelcomeAudio] = useState("");
  // welcomeMessage now holds the type: 'say' or 'play' (default 'say')
  const [welcomeMessage, setWelcomeMessage] = useState("say");
    const [OrgStatus, setOrgStatus] = useState("new");
    const [OrgActiveStatus, setOrgActiveStatus] = useState("new");

  // Disable account states
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  useEffect(() => {
    const loadMetrics = async () => {
      setIsLoadingMetrics(true);
      if (organizationFirebaseId) {
        let role = worker?.role;
        await fetchCallMetrics(organizationFirebaseId, role);
      }
      setIsLoadingMetrics(false);
    };
    loadMetrics();
  }, [organizationFirebaseId, worker?.role]);

  const MetricsSkeleton = () => (
    <SkeletonWrapper>
      <SkeletonLine width="60px" height="16px" marginBottom="8px" />
      <SkeletonLine width="40px" height="11px" />
    </SkeletonWrapper>
  );

  const totalItems = Array.isArray(organizations) ? organizations.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  // Ensure currentPage is within valid range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  // Calculate indices safely
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const indexOfLastItem = safeCurrentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Get current organizations safely
  const currentOrganizations = Array.isArray(organizations)
    ? organizations.slice(indexOfFirstItem, indexOfLastItem)
    : [];

  // Pagination navigation with safety checks
  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const nextPage = () => {
    if (safeCurrentPage < totalPages) {
      setCurrentPage(safeCurrentPage + 1);
    }
  };

  const prevPage = () => {
    if (safeCurrentPage > 1) {
      setCurrentPage(safeCurrentPage - 1);
    }
  };

  const handleNameClick = async (selectedOrganization) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setFilterByArray([]);
    setFilterValueArray([]);
    setNameClicked(selectedOrganization.name);
    setOrganizationNumber(selectedOrganization.assignedNumber);

    // ✅ NEW: Set to true when admin clicks on organization
    if (worker?.role?.includes("admin")) {
      setViewingAsOrganization(true);
      console.log(
        "🔍 Admin viewing as organization:",
        selectedOrganization.name,
      );
    }

    // Fetch the complete organization data from Firestore
    try {
      const q = query(
        collection(db, "organizations"),
        where("name", "==", selectedOrganization.name),
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        setOrganization({ ...doc.data(), id: doc.id });
      });
    } catch (error) {
      console.error("Error fetching complete organization data:", error);
      setOrganization(selectedOrganization);
    }

    if (organizationFirebaseId) {
      await fetchCallMetrics(organizationFirebaseId);
    }

    navigate("/home");
  };
  useEffect(() => {
    if (worker.role.includes("admin")) {
      setShowAllOrganizations(true);
    }
  }, [worker]);

  useEffect(() => {
    setShowReports(false);
    if (filterByArray.length > 0) {
      setOrganizationNumber(Object.values(organizationNumbers));
      setNameClicked("");
    } else if (filterByArray.length === 0 && !nameClicked) {
      setNameClicked("");
      setOrganizationNumber(process.env.REACT_APP_TWILIO_NUMBER);
    }
  }, [organizationNumbers]);

  // Function to handle row click and show profile modal
  const handleRowClick = (organization, e) => {
    // Prevent triggering if clicking on the name cell or settings cell
    if (
      e.target.tagName.toLowerCase() === "td" &&
      (e.target.classList.contains("name-cell") ||
        e.target.classList.contains("settings-cell") ||
        e.target.classList.contains("metrics-cell"))
    ) {
      return;
    }

    // Prevent triggering if clicking on settings icon or its parent
    if (
      e.target.closest(".settings-icon") ||
      e.target.closest(".settings-cell")
    ) {
      return;
    }

    // Fetch the full organization document (to ensure we have id/logoUrl) before showing modal
    const fetchAndShow = async (org) => {
      try {
        if (org.id) {
          const docRef = doc(db, "organizations", org.id);
          const docSnap = await getDoc(docRef);
          if (docSnap && docSnap.exists()) {
            setSelectedOrganization({ id: docSnap.id, ...docSnap.data() });
          } else {
            setSelectedOrganization(org);
          }
        } else if (org.name) {
          // Fallback: query by name
          const q = query(collection(db, "organizations"), where("name", "==", org.name));
          const querySnapshot = await getDocs(q);
          if (querySnapshot && !querySnapshot.empty) {
            const d = querySnapshot.docs[0];
            setSelectedOrganization({ id: d.id, ...d.data() });
          } else {
            setSelectedOrganization(org);
          }
        } else {
          setSelectedOrganization(org);
        }
      } catch (error) {
        console.error("Error fetching organization for modal:", error);
        setSelectedOrganization(organization);
      }
      setShowOrgModal(true);
    };

    fetchAndShow(organization);
  };

  // Function to close the organization modal
  const handleCloseOrgModal = () => {
    setShowOrgModal(false);
    setSelectedOrganization(null);
  };

  // Settings modal functions
  const handleSettingsClick = (organization, e) => {
    console.log(organization);

    e.stopPropagation();
    setSelectedOrgForSettings(organization);
    setShowSettingsModal(true);
    setIsPremium(organization.is_premium || false);
    setHoldMusic(organization.on_hold_audio || "");
    setWelcomeAudio(organization.welcomeAudioUrl || "");
    setOrgStatus(
      String(organization.status || "new").toLowerCase() === "active"
        ? "active"
        : "new"
    );
    // If the stored welcomeMessage is one of the new types use it, otherwise default to 'say'
    const wm = organization.welcomeMessage;
    setWelcomeMessage(wm === "play" || wm === "say" ? wm : "say");
      setZohoAccountId(organization.zohoAccountId || "");
    setOrgActiveStatus(
      String(organization.status || "new").toLowerCase() === "active"
        ? "active"
        : "new"
    );
  };

  const handleCloseSettingsModal = () => {
    setShowSettingsModal(false);
    setSelectedOrgForSettings(null);
    setShowDisableConfirm(false);
    setZohoAccountId("");
  };

  const handleSaveSettings = async () => {
    try {
      const trimmedZohoId = (
      zohoAccountId ||
      selectedOrgForSettings.zohoAccountId ||
      ""
    ).trim();
      const docRef = doc(db, "organizations", selectedOrgForSettings.id);
      await updateDoc(docRef, {
        is_premium: isPremium,
        on_hold_audio: holdMusic,
        welcomeAudioUrl: welcomeAudio,
        welcomeMessage: welcomeMessage,
        zohoAccountId: trimmedZohoId,
      });
      selectedOrgForSettings.is_premium = isPremium;
      selectedOrgForSettings.on_hold_audio = holdMusic;
      selectedOrgForSettings.welcomeAudioUrl = welcomeAudio;
      selectedOrgForSettings.welcomeMessage = welcomeMessage;
      selectedOrgForSettings.zohoAccountId = trimmedZohoId;
      

      console.log(OrgStatus + " -> OrgStatus, OrgActiveStatus -> " + OrgActiveStatus);

      if (OrgActiveStatus === 'new' && OrgStatus === 'active')
      {
          
      const params = new URLSearchParams({
            userName: selectedOrgForSettings.carePersonName,
            organizationName: selectedOrgForSettings.name,
            prayerLineNumber: selectedOrgForSettings.assignedNumber,
            userEmail: selectedOrgForSettings.carePersonEmail,
        });


      const apiUrl = process.env.REACT_APP_API_URL + "/send-welcome-email";
   

      const response = await fetch(`${apiUrl}?${params.toString()}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                Accept: "application/json",
            },
        });


          const res = await response.json();
          console.log(res);
          if (res.success === true)
          {
             await updateDoc(docRef, {status: OrgStatus});
             selectedOrgForSettings.status = OrgStatus;
             showSuccessMessage("Organization Welcome Email Sent successfully");
          }
          else
          {
             await updateDoc(docRef, {status: OrgActiveStatus});
             selectedOrgForSettings.status = OrgActiveStatus;
             showErrorMessage("Failed to sent Organization Welcome Email. Please try again!");
          }
      }
      
      showSuccessMessage("Organization settings saved successfully");
    } catch (error) {
      console.error("Error updating settings:", error);
      showErrorMessage("Failed to save organization settings");
      return;
    }
    handleCloseSettingsModal();
  };

  const handleDisableAccount = async () => {
    if (!selectedOrgForSettings) return;

    const trimmedZohoId = (
      zohoAccountId ||
      selectedOrgForSettings.zohoAccountId ||
      ""
    ).trim();
    if (!trimmedZohoId) {
      showErrorMessage(
        "Zoho Account ID is required before disabling. Save it in settings first.",
      );
      return;
    }

    setIsDisabling(true);
    try {
      const result = await disableOrganization({
        zohoAccountId: trimmedZohoId,
        firebaseId: selectedOrgForSettings.id,
        twilioNumber: selectedOrgForSettings.assignedNumber,
      });

      if (result.partial) {
        const errors = result.data?.errors || [];
        const errorDetails =
          errors.length > 0
            ? errors
                .map((e) => (typeof e === "string" ? e : JSON.stringify(e)))
                .join("; ")
            : "Some disable steps failed";
        showErrorMessage(`Organization partially disabled: ${errorDetails}`);
        setShowDisableConfirm(false);
        await filterOrganizations(filterByArray, filterValueArray);
        return;
      }

      showSuccessMessage(
        `${selectedOrgForSettings.name} has been disabled successfully`,
      );
      setShowDisableConfirm(false);
      handleCloseSettingsModal();
      await filterOrganizations(filterByArray, filterValueArray);
    } catch (error) {
      console.error("Error disabling account:", error);
      const message =
        error.response?.message ||
        error.message ||
        "Failed to disable organization";
      showErrorMessage(message);
    } finally {
      setIsDisabling(false);
    }
  };

  // Handle export options
  const handleExportOption = (e) => {
    const option = e.target.value;
    setExportOption(option);

    if (option === "print") {
      handlePrint();
    } else if (option === "export") {
      handleExport();
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentToPrint.current,
    documentTitle: "Organizations List",
    onAfterPrint: () => setExportOption(""),
  });

  // Direct access to metrics without any mapping
  const getMetricsForOrg = (organization) => {
    if (!callMetricsData?.data?.original?.data) {
      return { total_calls_all: 0, total_calls_ytd: 0, volunteers: 0 };
    }

    // Direct find in the original array
    const metricsItem = callMetricsData.data.original.data.find(
      (item) => item.firebase_id === organization.id,
    );

    return (
      metricsItem || { total_calls_all: 0, total_calls_ytd: 0, volunteers: 0 }
    );
  };

  const handleExport = () => {
    const dataToExport = organizations.map((org) => {
      const metrics = getMetricsForOrg(org);
      return {
        "Organization Name": org.name || "",
        Status: org.status === "disabled" ? "Disabled" : "Active",
        Type: org.type || "",
        Denomination: org.denomination || "",
        Tier: org.tier || "",
        "Province/State": org.province || "",
        Country: org.country || "",
        "Care Person": org.carePersonName || "",
        "Created At": org.createdAt
          ? new Date(org.createdAt.seconds * 1000).toLocaleDateString()
          : "",
        "Assigned Number": org.assignedNumber || "",
        "Prayer Partners Count": org.prayerPartnersCount || "",
        Website: org.website || "",
        "Phone Number": org.organizationPhoneNumber || "",
        "Total Calls Handled (Since onboarded)": metrics.total_calls_all || 0,
        "Total Calls Handled (YTD)": metrics.total_calls_ytd || 0,
        "Total Volunteers Onboarded": metrics.volunteers || 0,
      };
    });

    const csvData = Papa.unparse(dataToExport, { header: true });
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

    // Use file-saver to trigger the download
    saveAs(blob, "OrganizationsList.csv");
    setExportOption("");
  };

  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;
    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    selectElement.addEventListener("mousedown", handleMouseDown);
    const handleBlur = () => setIsSelectOpen(false);
    selectElement.addEventListener("blur", handleBlur);
    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  return (
    <>
      {Array.isArray(organizations) && (
        <>
          <div className="flex justify-between items-center mb-2 px-2">
            <PaginationText>
              {totalItems > 0
                ? `Showing ${indexOfFirstItem + 1}-${Math.min(
                    indexOfLastItem,
                    totalItems,
                  )} of ${totalItems} organizations`
                : "No organizations to display"}
            </PaginationText>

            {/* Export Options */}
            <SelectWrapper>
              <ExportSelect
                ref={selectRef}
                onChange={handleExportOption}
                value={exportOption}
              >
                <option value="">Export Options</option>
                <option value="print">Print PDF</option>
                <option value="export">Export as CSV</option>
              </ExportSelect>
              <ArrowIcon isOpen={isSelectOpen}>
                <ChevronDown />
              </ArrowIcon>
            </SelectWrapper>
          </div>

          <div ref={componentToPrint}>
            <Table className="p-2">
              <thead>
                <tr>
                  <th>Organization Name</th>
                  <th>Type</th>
                  <th>Denomination</th>
                  <th>Tier</th>
                  <th>Location</th>
                  <th>Care Person</th>
                  <th>Calls Handled</th>
                  <th>Volunteers</th>
                  <th>Settings</th>
                  {/*<th>Status</th>*/}
                </tr>
              </thead>
              <tbody>
                {currentOrganizations.map((organization, index) => {
                  const metrics = getMetricsForOrg(organization);
                  const isDisabled = organization.status === "disabled";
                  return (
                    <tr
                      key={index}
                      onClick={(e) => handleRowClick(organization, e)}
                      style={{ cursor: "pointer" }}
                      className={`organization-row${isDisabled ? " is-disabled" : ""}`}
                    >
                      <NameTD
                        className="name-cell"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNameClick(organization);
                        }}
                      >
                        <NameCellContent>
                          <span>
                            {organization.name.length > 30
                              ? `${organization.name.substring(0, 30)}...`
                              : organization.name}
                          </span>
                          {isDisabled && <DisabledBadge>Disabled</DisabledBadge>}
                        </NameCellContent>
                      </NameTD>
                      <td>
                        {organization.type.charAt(0).toUpperCase() +
                          organization.type.slice(1)}
                      </td>
                      <td>
                        {organization.denomination
                          ? organization.denomination.charAt(0).toUpperCase() +
                            organization.denomination.slice(1)
                          : "N/A"}
                      </td>
                      <td>
                        {organization.tier.charAt(0).toUpperCase() +
                          organization.tier.slice(1)}
                      </td>
                      <td>
                        <div className="flex flex-col">
                          <span>{organization.city}</span>
                          <span className="text-xs text-gray-500">
                          {organization.province},{organization.country}
                          </span>
                        </div>
                      </td>
                      <td>{organization.carePersonName}</td>
                      <MetricsTD className="metrics-cell">
                        {isLoadingMetrics ? (
                          <MetricsSkeleton />
                        ) : (
                          <CallsContainer>
                            <SinceOnboarded>
                              {metrics.total_calls_all?.toLocaleString() || 0}
                            </SinceOnboarded>
                            <YTDText>
                              YTD:{" "}
                              {metrics.total_calls_ytd?.toLocaleString() || 0}
                            </YTDText>
                          </CallsContainer>
                        )}
                      </MetricsTD>
                      <MetricsTD className="metrics-cell">
                        {isLoadingMetrics ? (
                          <MetricsSkeleton />
                        ) : (
                          metrics.volunteers?.toLocaleString() || 0
                        )}
                      </MetricsTD>
                      <SettingsTD className="settings-cell">
                        <SettingsIcon
                          className="settings-icon"
                          onClick={(e) => handleSettingsClick(organization, e)}
                        >
                          <Gear size={18} />
                        </SettingsIcon>
                          </SettingsTD>
                          {/* 
                          <SettingsTD className="settings-cell">
                        {String(organization.status || "new").toLowerCase() === "active" ? (
                          <span className="text-green-500">Active</span>
                        ) : (
                          <span className="text-red-500">New</span>
                        )}
                          </SettingsTD>*/}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 0 && (
            <div className="flex justify-center mt-4 mb-6">
              <PaginationControls>
                <button
                  onClick={prevPage}
                  disabled={safeCurrentPage === 1}
                  className={`pagination-button ${
                    safeCurrentPage === 1 ? "disabled" : ""
                  }`}
                >
                  <CaretLeft size={20} />
                </button>

                {/* Page numbers - only show a reasonable number of pages */}
                {totalPages <= 7 ? (
                  // If 7 or fewer pages, show all
                  [...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => paginate(index + 1)}
                      className={`pagination-number ${
                        safeCurrentPage === index + 1 ? "active" : ""
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))
                ) : (
                  // If more than 7 pages, show a subset with ellipsis
                  <>
                    {/* Always show first page */}
                    <button
                      onClick={() => paginate(1)}
                      className={`pagination-number ${
                        safeCurrentPage === 1 ? "active" : ""
                      }`}
                    >
                      1
                    </button>

                    {/* Show ellipsis if not near the start */}
                    {safeCurrentPage > 3 && (
                      <span className="pagination-ellipsis">...</span>
                    )}

                    {/* Show pages around current page */}
                    {[...Array(5)].map((_, index) => {
                      const pageNum = Math.max(2, safeCurrentPage - 2) + index;
                      // Only render if within range and not first/last page
                      if (pageNum > 1 && pageNum < totalPages) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`pagination-number ${
                              safeCurrentPage === pageNum ? "active" : ""
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}

                    {/* Show ellipsis if not near the end */}
                    {safeCurrentPage < totalPages - 2 && (
                      <span className="pagination-ellipsis">...</span>
                    )}

                    {/* Always show last page */}
                    <button
                      onClick={() => paginate(totalPages)}
                      className={`pagination-number ${
                        safeCurrentPage === totalPages ? "active" : ""
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={nextPage}
                  disabled={safeCurrentPage === totalPages}
                  className={`pagination-button ${
                    safeCurrentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <CaretRight size={20} />
                </button>
              </PaginationControls>
            </div>
          )}
        </>
      )}

      {/* Use the new OrganizationDetailsModal component */}
      {selectedOrganization && (
        <OrganizationDetailsModal
          organization={selectedOrganization}
          show={showOrgModal}
          handleClose={handleCloseOrgModal}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal &&
        selectedOrgForSettings &&
        createPortal(
          <SettingsModalOverlay onClick={handleCloseSettingsModal}>
            <SettingsModalContent onClick={(e) => e.stopPropagation()}>
              <SettingsModalBody>
              <OrganizationInfoSection>
                <SectionHeader>Organization Details</SectionHeader>
                <OrganizationCard>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Name:</InfoLabel>
                      <InfoValue>{selectedOrgForSettings.name}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Type:</InfoLabel>
                      <InfoValue>
                        {selectedOrgForSettings.type?.charAt(0).toUpperCase() +
                          selectedOrgForSettings.type?.slice(1)}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Denomination:</InfoLabel>
                      <InfoValue>
                        {selectedOrgForSettings.denomination || "N/A"}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Tier:</InfoLabel>
                      <InfoValue>
                        {selectedOrgForSettings.tier?.charAt(0).toUpperCase() +
                          selectedOrgForSettings.tier?.slice(1)}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Location:</InfoLabel>
                      <InfoValue>
                        {selectedOrgForSettings.province},{" "}
                        {selectedOrgForSettings.country}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Care Person:</InfoLabel>
                      <InfoValue>
                        {selectedOrgForSettings.carePersonName}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Assigned Number:</InfoLabel>
                      <InfoValue>
                        {selectedOrgForSettings.assignedNumber}
                      </InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </OrganizationCard>
              </OrganizationInfoSection>

              {!selectedOrgForSettings.zohoAccountId?.trim() && (
                <ZohoSection>
                  <ZohoSectionHeader>Zoho Integration</ZohoSectionHeader>
                  <SettingsCard>
                    <SettingRow>
                      <SettingInfo>
                        <SettingTitle>Zoho Account ID</SettingTitle>
                        <SettingDescription>
                          Required to disable this organization. Enter the Zoho CRM
                          Account ID and save before disabling.
                        </SettingDescription>
                      </SettingInfo>
                    </SettingRow>
                    <ZohoInputWrapper>
                      <ZohoAccountInput
                        type="text"
                        placeholder="e.g. 32916000001603441"
                        value={zohoAccountId}
                        onChange={(e) => setZohoAccountId(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        autoComplete="off"
                      />
                    </ZohoInputWrapper>
                  </SettingsCard>
                </ZohoSection>
              )}

              <CallReportSection>
                <CallReportHeader>Call Report Settings</CallReportHeader>
                <SettingsCard>
                  <SettingRow>
                    <SettingInfo>
                      <SettingTitle>Premium Access</SettingTitle>
                      <SettingDescription>
                        Enable premium features for this organization.
                      </SettingDescription>
                    </SettingInfo>
                    <ToggleContainer>
                      <ToggleButton
                        isEnabled={isPremium}
                        onClick={() => setIsPremium(!isPremium)}
                      >
                        <ToggleSlider isEnabled={isPremium} />
                      </ToggleButton>
                      <ToggleLabel isEnabled={isPremium}>
                        {isPremium ? "Enabled" : "Disabled"}
                      </ToggleLabel>
                    </ToggleContainer>
                  </SettingRow>
                </SettingsCard>
              </CallReportSection>

              <HoldMusicSection>
                <HoldMusicHeader>Set Custom On-Hold Audio</HoldMusicHeader>
                <SettingsCard>
                  <SettingRow>
                    <SettingInfo>
                      <SettingTitle>Custom On-Hold Audio</SettingTitle>
                      <SettingDescription>
                        Enter a URL to a custom audio file to play when callers
                        are placed on hold.
                      </SettingDescription>
                    </SettingInfo>
                  </SettingRow>
                  <HoldMusicInputWrapper>
                    <HoldMusicInput
                      type="text"
                      placeholder="https://example.com/hold-music.mp3"
                      value={holdMusic}
                      onChange={(e) => setHoldMusic(e.target.value)}
                    />
                  </HoldMusicInputWrapper>
                </SettingsCard>
                        </HoldMusicSection>

               

              {/* New: Welcome Audio Section (duplicate of Hold Music) */}
              <WelcomeAudioSection>
                <WelcomeAudioHeader>Set Custom Welcome Audio</WelcomeAudioHeader>
                <SettingsCard>
                  <SettingRow style={{ marginTop: 12 }}>
                    <SettingInfo>
                      <SettingTitle>Welcome Message</SettingTitle>
                      <SettingDescription>
                        Choose whether the welcome should be spoken (Say) or play an audio file (Play). If you select "Play", provide the audio URL below.
                      </SettingDescription>
                    </SettingInfo>
                    <div>
                      <WelcomeSelect
                        value={welcomeMessage}
                        onChange={(e) => setWelcomeMessage(e.target.value)}
                      >
                        <option value="say">Say</option>
                        <option value="play">Play</option>
                      </WelcomeSelect>
                    </div>
                  </SettingRow>

                  {/* Show audio title/description and URL input only when 'play' is selected */}
                  {welcomeMessage === "play" && (
                    <>
                      <SettingRow style={{ marginTop: 16 }}>
                        <SettingInfo>
                          <SettingTitle>Welcome Audio URL</SettingTitle>
                          <SettingDescription>
                            Enter a URL to a custom audio file to play as the welcome
                            message for callers.
                          </SettingDescription>
                        </SettingInfo>
                      </SettingRow>
                      <WelcomeAudioInputWrapper>
                        <WelcomeAudioInput
                          type="text"
                          placeholder="https://example.com/welcome-audio.mp3"
                          value={welcomeAudio}
                          onChange={(e) => setWelcomeAudio(e.target.value)}
                        />
                      </WelcomeAudioInputWrapper>
                    </>
                  )}
                </SettingsCard>
                </WelcomeAudioSection>

                <OrgActivationSection>
                    <OrgActivationHeader>Organization Activation</OrgActivationHeader>
                    <OrgActivationCard>
                        <SettingRow style={{ marginTop: 12 }}>
                            <SettingInfo>
                                <SettingTitle>Activate New Organization</SettingTitle>
                                <SettingDescription>
                                    Select "Active" only when this organization is fully configured, tested, and ready to go live.
                                    <br/><span style={{ color: "red" }}>Note: Once activated, the subscriber will receive a welcome email with onboarding instructions</span><br />
                                </SettingDescription>
                            </SettingInfo>
                            <div>
                                <WelcomeSelect
                                    value={OrgStatus}
                                            onChange={(e) => setOrgStatus(e.target.value)}
                                            disabled={OrgActiveStatus === "active"}
                                >
                                    <option value="new">New</option>
                                    <option value="active">Active</option>
                                </WelcomeSelect>
                            </div>
                        </SettingRow>
                    </OrgActivationCard>
                </OrgActivationSection>

              <DangerZoneSection>
                <DangerZoneHeader>Disable Account</DangerZoneHeader>
                <DangerCard>
                  <SettingRow>
                    <SettingInfo>
                      <DangerTitle>
                        {selectedOrgForSettings.status === "disabled"
                          ? "Account Disabled"
                          : "Disable this account"}
                      </DangerTitle>
                      <SettingDescription>
                        {selectedOrgForSettings.status === "disabled"
                          ? `This account was disabled${
                              selectedOrgForSettings.disabledAt
                                ? ` on ${new Date(
                                    selectedOrgForSettings.disabledAt.seconds
                                      ? selectedOrgForSettings.disabledAt
                                          .seconds * 1000
                                      : selectedOrgForSettings.disabledAt,
                                  ).toLocaleString()}`
                                : ""
                            }.`
                          : "Disabling routes the prayer line to unavailable, clears subscriber Auth0 access, updates Zoho to Cancelled Subscriber, and marks this organization as disabled. This action cannot be easily undone."}
                      </SettingDescription>
                      {selectedOrgForSettings.status !== "disabled" &&
                        !(
                          zohoAccountId.trim() ||
                          selectedOrgForSettings.zohoAccountId?.trim()
                        ) && (
                          <ZohoRequiredHint>
                            Zoho Account ID is required before disabling. Save it
                            above.
                          </ZohoRequiredHint>
                        )}
                    </SettingInfo>
                    <DisableButton
                      disabled={
                        selectedOrgForSettings.status === "disabled" ||
                        !(
                          zohoAccountId.trim() ||
                          selectedOrgForSettings.zohoAccountId?.trim()
                        )
                      }
                      onClick={() => setShowDisableConfirm(true)}
                    >
                      {selectedOrgForSettings.status === "disabled"
                        ? "Disabled"
                        : "Disable Account"}
                    </DisableButton>
                  </SettingRow>
                </DangerCard>
              </DangerZoneSection>
            </SettingsModalBody>

            <SettingsModalFooter>
              <CancelButton onClick={handleCloseSettingsModal}>
                Cancel
              </CancelButton>
              <SaveButton onClick={handleSaveSettings}>
                Save Settings
              </SaveButton>
            </SettingsModalFooter>
          </SettingsModalContent>
        </SettingsModalOverlay>,
          document.body,
        )}

      {/* Disable Account Confirmation */}
      {showDisableConfirm && selectedOrgForSettings && (
        <ConfirmOverlay>
          <ConfirmContent>
            <ConfirmHeader>Disable Account?</ConfirmHeader>
            <ConfirmText>
              Are you sure you want to disable{" "}
              <strong>{selectedOrgForSettings.name}</strong>?
            </ConfirmText>
            <ConfirmWarning>
              This will route the prayer line to unavailable, clear subscriber
              Auth0 metadata (fullName, organizationName, role), set Zoho to
              Cancelled Subscriber, and mark the organization as{" "}
              <strong>disabled</strong>. This action cannot be easily undone.
            </ConfirmWarning>
            <ConfirmFooter>
              <CancelButton
                onClick={() => setShowDisableConfirm(false)}
                disabled={isDisabling}
              >
                Cancel
              </CancelButton>
              <ConfirmDisableButton
                onClick={handleDisableAccount}
                disabled={isDisabling}
              >
                {isDisabling ? "Disabling..." : "Yes, Disable Account"}
              </ConfirmDisableButton>
            </ConfirmFooter>
          </ConfirmContent>
        </ConfirmOverlay>
      )}
    </>
  );
};

const PaginationText = styled.span`
  color: #ff7234;
  text-align: center;
  font-size: 16px;
  font-family: "Futura PT", sans-serif;
  letter-spacing: 0.1em;
  font-weight: 400;
  line-height: normal;
`;

const WelcomeSelect = styled.select`
  width: 140px;
  padding: 8px 10px;
  border: 1.5px solid #ffd4c4;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: #fff;
  font-family: "Futura PT", sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff7234;
    box-shadow: 0 0 0 3px rgba(255, 114, 52, 0.15);
  }
`;

const Table = styled.table`
  width: 100%;
  border: 1px solid #00323b;
  thead {
    font-family: "Futura PT", sans-serif;
    font-size: 16px;
    font-weight: 700;
    flex-shrink: 0;
    border-bottom: 1px solid #00323b;
    background-color: #f1f1f1;
    color: #178b96;
    text-align: center;
    height: 45px;
    letter-spacing: 0.1em;

    th {
      padding: 12px 8px;
      vertical-align: middle;
    }
  }
  tbody {
    text-align: center;
    tr {
      &.is-disabled {
        opacity: 0.65;

        td {
          background-color: #f3f4f6;
          color: #6b7280;
        }

        .metrics-cell {
          color: #9ca3af;
        }
      }
    }
    td {
      padding: 0.5em;
      border-bottom: 1px solid #00323b;
      background-color: #fff;
      color: #178b96;
      font-family: "Futura PT", sans-serif;
      font-size: 16px;
      font-weight: 400;
      letter-spacing: 0.1em;
    }
  }
`;
const NameTD = styled.td`
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const NameCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DisabledBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #fee2e2;
  color: #b91c1c;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1.4;
  white-space: nowrap;
`;

const MetricsTD = styled.td`
  font-weight: 600;
  color: #ff7234;
  font-size: 15px;
`;

// Skeleton loading styled components
const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const SkeletonLine = styled.div`
  width: ${(props) => props.width || "50px"};
  height: ${(props) => props.height || "16px"};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: ${(props) => props.marginBottom || "0"};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// New styled components for merged calls column
const CallsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const SinceOnboarded = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #178b96;
`;

const YTDText = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #178b96;
`;

const SettingsTD = styled.td`
  text-align: center;
  vertical-align: middle;
`;

const SettingsIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f1f1f1;
  color: #178b96;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #178b96;
    color: white;
    transform: scale(1.1);
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .pagination-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background-color: #f1f1f1;
    color: #178b96;
    cursor: pointer;

    &:hover {
      background-color: #bfe9ee;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background-color: #f1f1f1;
      }
    }
  }

  .pagination-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background-color: #f1f1f1;
    color: #178b96;
    cursor: pointer;

    &:hover {
      background-color: #bfe9ee;
    }

    &.active {
      background-color: #178b96;
      color: white;
    }
  }

  .pagination-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: #178b96;
  }
`;

// Settings Modal Styled Components
const SettingsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const SettingsModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
`;

const SettingsModalBody = styled.div`
  padding: 25px;
`;

const OrganizationInfoSection = styled.div`
  margin-bottom: 10px;
`;

const SectionHeader = styled.h4`
  color: #178b96;
  margin-bottom: 15px;
  font-family: Recoleta, serif;
  font-size: 18px;
  text-decoration: underline;
  text-underline-offset: 4px;
`;

const OrganizationCard = styled.div`
  background: #f8feff;
  border: 1px solid #bfe9ee;
  border-radius: 8px;
  padding: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  color: #106271;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  color: #178b96;
  font-weight: 500;
  font-size: 15px;
`;

const CallReportSection = styled.div`
  margin-bottom: 10px;
`;

const CallReportHeader = styled.h4`
  color: #ff7234;
  margin-bottom: 15px;
  font-family: Recoleta, serif;
  font-size: 18px;
  text-decoration: underline;
  text-underline-offset: 4px;
`;

const SettingsCard = styled.div`
  background: #fffaf8;
  border: 1px solid #ffd4c4;
  border-radius: 8px;
  padding: 20px;
`;

const OrgActivationCard = styled.div`
  background: #BCE7EA;
  border: 1px solid #ffd4c4;
  border-radius: 8px;
  padding: 20px;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingTitle = styled.h5`
  margin: 0 0 8px 0;
  color: #ff7234;
  font-size: 16px;
  font-weight: 600;
`;

const SettingDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleButton = styled.button`
  width: 56px;
  height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  background: ${(props) => (props.isEnabled ? "#178B96" : "#ddd")};
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.05);
    background: ${(props) => (props.isEnabled ? "#146b75" : "#ccc")};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(23, 139, 150, 0.3);
  }
`;

const ToggleSlider = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: ${(props) => (props.isEnabled ? "31px" : "3px")};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ToggleLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => (props.isEnabled ? "#178B96" : "#666")};
  min-width: 60px;
`;

const SettingsModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  padding: 20px 25px;
  background: #f8feff;
  border-top: 1px solid #bfe9ee;
  border-radius: 0 0 6px 6px;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  border: 2px solid #178b96;
  background: white;
  color: #178b96;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  &:hover {
    background: #f8feff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(23, 139, 150, 0.2);
  }
`;
const SaveButton = styled.button`
  padding: 12px 24px;
  border: none;
  background: linear-gradient(135deg, #178b96 0%, #106271 100%);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  &:hover {
    background: linear-gradient(135deg, #146b75 0%, #0d4d56 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(23, 139, 150, 0.3);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
  }
`;
const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk", sans-serif;
  border-radius: 8px;
  width: 172px;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 30px 0 10px;
  &:focus {
    outline: none;
    border-color: #106271;
  }
  &:hover {
    background: #f0f0f0;
  }
`;
const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #106271;
  pointer-events: none;
  transition: transform 0.3s ease;
  svg {
    height: 20px;
    width: 20px;
  }
`;
const HoldMusicSection = styled.div`
  margin-bottom: 20px;
`;

const OrgActivationSection = styled.div`
  margin-bottom: 20px;
`;

const ZohoSection = styled.div`
  margin-bottom: 20px;
`;

const ZohoSectionHeader = styled.h4`
  color: #178b96;
  margin-bottom: 15px;
  font-family: Recoleta, serif;
  font-size: 18px;
  text-decoration: underline;
  text-underline-offset: 4px;
`;

const ZohoInputWrapper = styled.div`
  margin-top: 15px;
`;

const ZohoAccountInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #bfe9ee;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: #fff;
  font-family: "Futura PT", sans-serif;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  box-sizing: border-box;
  pointer-events: auto;
  cursor: text;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: #178b96;
    box-shadow: 0 0 0 3px rgba(23, 139, 150, 0.15);
  }

  &:hover {
    border-color: #8ddce8;
  }
`;
const OrgActivationHeader = styled.h4`
  color: #ff7234;
  margin-bottom: 15px;
  font-family: Recoleta, serif;
  font-size: 18px;
  text-decoration: underline;
  text-underline-offset: 4px;
`;
const HoldMusicHeader = styled.h4`
  color: #ff7234;
  margin-bottom: 15px;
  font-family: Recoleta, serif;
  font-size: 18px;
  text-decoration: underline;
  text-underline-offset: 4px;
`;
const HoldMusicInputWrapper = styled.div`
  margin-top: 15px;
`;
const HoldMusicInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #ffd4c4;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: #fff;
  font-family: "Futura PT", sans-serif;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: #ff7234;
    box-shadow: 0 0 0 3px rgba(255, 114, 52, 0.15);
  }

  &:hover {
    border-color: #ffb89a;
  }
`;

const ZohoRequiredHint = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #d9342b;
  font-family: "Futura PT", sans-serif;
`;

// Aliases for welcome audio using same styles as hold music for clarity
const WelcomeAudioSection = HoldMusicSection;
const WelcomeAudioHeader = HoldMusicHeader;
const WelcomeAudioInputWrapper = HoldMusicInputWrapper;
const WelcomeAudioInput = HoldMusicInput;

// Disable Account (Danger Zone) Styled Components
const DangerZoneSection = styled.div`
  margin-bottom: 10px;
`;

const DangerZoneHeader = styled.h4`
  color: #d9342b;
  margin-bottom: 15px;
  font-family: Recoleta, serif;
  font-size: 18px;
  text-decoration: underline;
  text-underline-offset: 4px;
`;

const DangerCard = styled.div`
  background: #fff5f5;
  border: 1px solid #f5c2c0;
  border-radius: 8px;
  padding: 20px;
`;

const DangerTitle = styled.h5`
  margin: 0 0 8px 0;
  color: #d9342b;
  font-size: 16px;
  font-weight: 600;
`;

const DisableButton = styled.button`
  padding: 12px 20px;
  border: none;
  background: #d9342b;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #b82a23;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(217, 52, 43, 0.3);
  }

  &:disabled {
    background: #e0a8a4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;

const ConfirmContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 460px;
  padding: 25px;
`;

const ConfirmHeader = styled.h4`
  color: #d9342b;
  margin: 0 0 15px 0;
  font-family: Recoleta, serif;
  font-size: 20px;
`;

const ConfirmText = styled.p`
  margin: 0 0 12px 0;
  color: #333;
  font-size: 15px;
  line-height: 1.5;
`;

const ConfirmWarning = styled.p`
  margin: 0 0 20px 0;
  color: #b82a23;
  background: #fff5f5;
  border: 1px solid #f5c2c0;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.5;
`;

const ConfirmFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;

const ConfirmDisableButton = styled.button`
  padding: 12px 24px;
  border: none;
  background: #d9342b;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #b82a23;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(217, 52, 43, 0.3);
  }

  &:disabled {
    background: #e0a8a4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;